import React, { useCallback, useEffect, useState, useMemo } from "react";
import styled, { withTheme } from "styled-components";
import {
  DateFormat,
  studyStatusOptions,
  TIME_FORMATS,
} from "../../../modules/constants";
import CustomDropdown from "../../ui-library/CustomDropdown/CustomDropdown";
import LabelledInput from "../../ui-library/LabelledInput/LabelledInput";
import SetDailyGoals from "../SetDailyGoals/SetDailyGoals";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import { isNumeric } from "../../../modules/utils";
import Checkbox from "../../ui-library/Checkbox/Checkbox";
import { DEFAULT_LOCALE, getLanguages } from "../../../i18n/helpers";
import DateSelect from "../../ui-library/DateAndTime/Date/DateSelect";
import moment from "moment";

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledContainerHorizontal = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  @media (max-width: 576px) {
    padding-top: 2rem;
    margin-top: auto;
    .btn {
      width: 100%;
    }
  }
`;

const StyledLabel = styled.label`
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
`;

const StyledError = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme?.cardinal};
  text-transform: uppercase;
`;

const DatePickerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  &.month-select {
    .react-datepicker__month-container > .react-datepicker__header,
    .react-datepicker__navigation {
      display: none;
    }
    .react-datepicker__month {
      margin: 1rem 0.4rem;
    }
  }
`;

const SetDailyGoalsWrapper = styled.div`
  margin-bottom: 15px;
`;

const minDate = 1900;
const presentYear = new Date().getFullYear();

const numberRegex = "[0-9]{3}";
const ugnxIdRegex = new RegExp(`^${numberRegex}(-${numberRegex})$`);
const ugnxIdInputRegex = new RegExp("^([0-9]{1,3}-)(?:[0-9]{1,3})?$");
const yearRegex = new RegExp("^[0-9]{1,4}$");

const AddParticipant = (props) => {
  const { onSubmit, studyId, siteId, hasError } = props;

  const [ugnxId, setUgnxId] = useState("");
  const [yearOfBirth, setYearOfBirth] = useState("");
  const [isConsentSigned, setIsConsentSigned] = useState(false);
  const [isIdVerified, setIsIdVerified] = useState(false);
  const [isDiagnosisConfirmed, setIsDiagnosisConfirmed] = useState(false);
  const [timeFormat, setTimeFormat] = useState(TIME_FORMATS[0]);
  const [language, setLanguage] = useState(DEFAULT_LOCALE);

  const [showErrorDate, setShowErrorDate] = useState(false);
  const [isValidDate, setIsValidDate] = useState(true);
  const [dateOfEnrollment, setDateOfEnrollment] = useState(null);

  const [dailyGoals, setDailyGoals] = useState(null);
  const [isDailyGoalsValid, setIsDailyGoalsValid] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const availableLanguages = useMemo(
    () => getLanguages().filter((language) => language.isAvailable),
    []
  );

  const errorHandling = {
    showErrorDate: showErrorDate,
    setShowErrorDate: setShowErrorDate,
    isValidDate: isValidDate,
    setIsValidDate: setIsValidDate,
  };

  const [errors, setErrors] = useState({});

  const handleUgnxIdOnChange = (value) => {
    const ugnxIDFirstNumbersRegex = new RegExp(`^${numberRegex}$`);

    if (isNumeric(value)) {
      //avoid adding "-" if the user is deleting chars
      if (
        ugnxId.length < value.length &&
        new RegExp(ugnxIDFirstNumbersRegex).test(value)
      ) {
        setUgnxId(`${value}-`);
      } else if (value.length >= 4 && !value.includes("-")) {
        setUgnxId(
          `${value.substring(0, 3)}-${value.substring(3, value.length)}`
        );
      } else {
        setUgnxId(value);
      }
    } else if (value === "" || ugnxIdInputRegex.test(value)) {
      setUgnxId(value);
    }
  };

  const handleYearOfBirthDateChange = (value) => {
    if (value === "" || (value && yearRegex.test(value))) {
      setYearOfBirth(value);
    }
  };

  const handleDateOfEnrollmentChange = (value) => {
    if (value) {
      validateDateOfEnrollment(value);
      setDateOfEnrollment(value);
    }
  };

  const handleConsentSignedOnchange = (value) => {
    validateConsentSigned(!value);
    setIsConsentSigned(!value);
  };

  const handleVerifiedIDOnChange = (value) => {
    validateVerifiedID(!value);
    setIsIdVerified(!value);
  };

  const handleDiagnosisConfirmedChange = (value) => {
    validateDiagnosisConfirmed(!value);
    setIsDiagnosisConfirmed(!value);
  };

  useEffect(() => {
    if (hasError) {
      setSubmitting(false);
    }
  }, [hasError]);

  const validateYearBirth = () => {
    setErrors({
      ...errors,
      yearOfBirth: isYearBirthValid()
        ? undefined
        : `The date must be between ${minDate} and ${presentYear}`,
    });
  };

  const validateUgnxId = () => {
    if (ugnxId === "") {
      setErrors({
        ...errors,
        ugnxId: isUgnxIdValid() ? undefined : "This field is required.",
      });
    } else {
      setErrors({
        ...errors,
        ugnxId: isUgnxIdValid() ? undefined : "Invalid format, must be XXX-XXX",
      });
    }
  };

  const validateConsentSigned = (value) => {
    setErrors({
      ...errors,
      isConsentSigned: isConsentSignedValid(value)
        ? undefined
        : "This field is required.",
    });
  };

  const validateVerifiedID = (value) => {
    setErrors({
      ...errors,
      isIdVerified: isVerifiedIDValid(value)
        ? undefined
        : "This field is required.",
    });
  };

  const validateDiagnosisConfirmed = (value) => {
    setErrors({
      ...errors,
      isDiagnosisConfirmed: isDiagnosisConfirmedValid(value)
        ? undefined
        : "This field is required.",
    });
  };

  const validateDateOfEnrollment = (value) => {
    setErrors({
      ...errors,
      dateOfEnrollment: isDateOfEnrollmentValid(value)
        ? undefined
        : "This field is required.",
    });
  };

  const onChangeDailyTargets = (isSetDailyGoalsValid, goals) => {
    setIsDailyGoalsValid(isSetDailyGoalsValid);
    setDailyGoals(isSetDailyGoalsValid ? goals : null);
  };

  const isYearBirthValid = useCallback(() => {
    if (yearOfBirth < minDate || yearOfBirth > presentYear) {
      return false;
    }
    return true;
  }, [yearOfBirth]);

  const isUgnxIdValid = useCallback(() => {
    if (!ugnxIdRegex.test(ugnxId)) {
      return false;
    }
    return true;
  }, [ugnxId]);

  const isConsentSignedValid = useCallback((value) => {
    return value;
  }, []);

  const isVerifiedIDValid = useCallback((value) => {
    return value;
  }, []);

  const isDiagnosisConfirmedValid = useCallback((value) => {
    return value;
  }, []);

  const isDateOfEnrollmentValid = useCallback((value) => {
    return !!value;
  }, []);

  const submitForm = useCallback(() => {
    const formData = {
      studyId,
      siteId,
      ugnxId,
      yearOfBirth: parseInt(yearOfBirth),
      signedConsent: isConsentSigned,
      idVerified: isIdVerified,
      diagnosisConfirmed: isDiagnosisConfirmed,
      language,
      studyStatus: studyStatusOptions.SCREENING.id,
      dailyTarget: dailyGoals,
      dateOfEnrollment: moment(dateOfEnrollment).format(
        DateFormat.EXTENDED_DATETIME
      ),
      timeFormat: timeFormat.id,
    };

    setSubmitting(true);
    onSubmit(formData);
  }, [
    studyId,
    siteId,
    ugnxId,
    yearOfBirth,
    isConsentSigned,
    isIdVerified,
    isDiagnosisConfirmed,
    language,
    dailyGoals,
    onSubmit,
    timeFormat,
    dateOfEnrollment,
  ]);

  const canSubmit = useMemo(() => {
    if (
      isYearBirthValid() &&
      isUgnxIdValid() &&
      isConsentSignedValid(isConsentSigned) &&
      isVerifiedIDValid(isIdVerified) &&
      isDiagnosisConfirmedValid(isDiagnosisConfirmed) &&
      isDateOfEnrollmentValid(dateOfEnrollment) &&
      isDailyGoalsValid
    ) {
      return true;
    }

    return false;
  }, [
    isYearBirthValid,
    isUgnxIdValid,
    isConsentSignedValid,
    isConsentSigned,
    isVerifiedIDValid,
    isIdVerified,
    isDiagnosisConfirmedValid,
    isDiagnosisConfirmed,
    isDateOfEnrollmentValid,
    dateOfEnrollment,
    isDailyGoalsValid,
  ]);

  return (
    <FormContainer data-testid="addnewparticipantId">
      <LabelledInput
        tabIndex={1}
        data-testid="ugnxID-input"
        className="mt-4"
        title="Participant ID"
        value={ugnxId}
        type="text"
        placeholder="XXX-XXX"
        hasError={!!errors?.ugnxId}
        errorMessage={errors?.ugnxId}
        onChange={(e) => handleUgnxIdOnChange(e.target.value)}
        onBlur={() => validateUgnxId()}
        autoFocus={true}
      />

      <LabelledInput
        tabIndex={2}
        data-testid="yearOfBirth-input"
        className="mt-4"
        title="Year of birth"
        min={minDate}
        max={presentYear}
        value={yearOfBirth}
        type="text"
        placeholder="YYYY"
        pattern="[0-9]*"
        hasError={!!errors?.yearOfBirth}
        errorMessage={errors?.yearOfBirth}
        onChange={(e) => {
          e.target.validity.valid &&
            handleYearOfBirthDateChange(e.target.value);
        }}
        onBlur={() => validateYearBirth()}
      />

      <div>
        <DatePickerContainer className="mt-4">
          <StyledLabel className="mb-0 me-2">Date of Consent</StyledLabel>
          <DateSelect
            eventDate={dateOfEnrollment}
            setEventDate={handleDateOfEnrollmentChange}
            displayTitle={false}
            errorHandling={errorHandling}
            maxDate={new Date()}
            showMonthDropdown
            showYearDropdown
          />
        </DatePickerContainer>
        <StyledError>
          {errors?.dateOfEnrollment && <span>{errors?.dateOfEnrollment}</span>}
        </StyledError>
      </div>

      <span className="mt-4">
        <Checkbox
          tabIndex={3}
          data-testid="consentSigned-checkbox"
          checked={isConsentSigned}
          onClick={(e) => handleConsentSignedOnchange(isConsentSigned)}
          icon={true}
          square={true}
        />
        <StyledLabel
          className="ms-2"
          onClick={(e) => handleConsentSignedOnchange(isConsentSigned)}
        >
          Informed consent signed
        </StyledLabel>
      </span>
      <StyledError>
        {errors?.isConsentSigned && <span>{errors?.isConsentSigned}</span>}
      </StyledError>

      <span className="mt-2">
        <Checkbox
          tabIndex={4}
          data-testid="verifiedID-checkbox"
          checked={isIdVerified}
          onClick={(e) => handleVerifiedIDOnChange(isIdVerified)}
          icon={true}
          square={true}
        />
        <StyledLabel
          className="ms-2"
          onClick={(e) => handleVerifiedIDOnChange(isIdVerified)}
        >
          ID verified
        </StyledLabel>
      </span>
      <StyledError>
        {errors?.isIdVerified && <span>{errors?.isIdVerified}</span>}
      </StyledError>

      <span className="mt-2">
        <Checkbox
          tabIndex={5}
          data-testid="diagnosis-confirmed-checkbox"
          checked={isDiagnosisConfirmed}
          onClick={(e) => handleDiagnosisConfirmedChange(isDiagnosisConfirmed)}
          icon={true}
          square={true}
        />
        <StyledLabel
          className="ms-2"
          onClick={(e) => handleDiagnosisConfirmedChange(isDiagnosisConfirmed)}
        >
          Diagnosis confirmed
        </StyledLabel>
      </span>
      <StyledError>
        {errors?.isDiagnosisConfirmed && (
          <span>{errors?.isDiagnosisConfirmed}</span>
        )}
      </StyledError>

      <StyledContainerHorizontal className="mt-4">
        <StyledLabel className="me-2">Time format</StyledLabel>
        <CustomDropdown
          data-testid="timeformat-dropdown"
          custom-dropdown-menu-data-testid="timeformat-options-dropdown"
          tabindexprop={7}
          value={timeFormat.value}
          options={TIME_FORMATS}
          width={145}
          onChange={(timeFormat) => setTimeFormat(timeFormat)}
        />
      </StyledContainerHorizontal>

      <StyledContainerHorizontal className="mt-4">
        <StyledLabel className="me-2">Select language</StyledLabel>
        <CustomDropdown
          data-testid="language-dropdown"
          custom-dropdown-menu-data-testid="language-options-dropdown"
          tabindexprop={7}
          placeholder="Select"
          value={getLanguages().find((lang) => lang.code === language)?.value}
          options={availableLanguages}
          width={145}
          onChange={(lang) => setLanguage(lang.code)}
        />
      </StyledContainerHorizontal>

      <StyledContainerHorizontal className="mt-4">
        <StyledLabel className="mb-0 me-2">Study Status</StyledLabel>
        <CustomDropdown
          placeholder={studyStatusOptions.SCREENING.value}
          disabled
          width={160}
          data-testid="study-status-dropdown"
        />
      </StyledContainerHorizontal>

      <SetDailyGoalsWrapper className="mt-4">
        <SetDailyGoals onChange={onChangeDailyTargets} />
      </SetDailyGoalsWrapper>

      <StyledFooter className="mt-4">
        <PrimaryButton
          disabled={!canSubmit || submitting}
          tabIndex={8}
          data-testid="submitparticipant"
          onClick={submitForm}
        >
          Add Participant And Set Targets
        </PrimaryButton>
      </StyledFooter>
    </FormContainer>
  );
};

export default withTheme(AddParticipant);
